/**
 * Which Fatoraty environment this page is running in, and the URLs that go with it.
 *
 * One build serves every environment: opened on a host with "staging" in its name it is staging,
 * on localhost (or under npm start) it is development, anywhere else it is production - so the
 * default is fatoraty.com. REACT_APP_ENV forces the environment, and an explicit REACT_APP_SERVER_URL,
 * REACT_APP_URL or REACT_APP_MOYASAR_CALLBACK_URL replaces that one URL (see .env.development).
 *
 * Read the URLs from here, never from process.env or a hard-coded host.
 */
const APP_ENVIRONMENTS = ["production", "staging", "development"];

const URL_DEFAULTS = {
  production: {
    serverUrl: "https://api.fatoraty.com/",
    appUrl: "https://fatoraty.com/",
    moyasarCallbackUrl: "https://fatoraty.com/subscription",
  },
  staging: {
    serverUrl: "https://api.staging.fatoraty.com/",
    appUrl: "https://staging.fatoraty.com/",
    moyasarCallbackUrl: "https://staging.fatoraty.com/subscription",
  },
  development: {
    serverUrl: "http://localhost:3001/",
    appUrl: "http://localhost:3000/",
    moyasarCallbackUrl: "http://localhost:3000/subscription",
  },
};

const LOCAL_HOSTNAMES = ["localhost", "127.0.0.1"];

const resolveAppEnv = (env, hostname) => {
  const explicit = (env.REACT_APP_ENV || "").trim().toLowerCase();
  if (APP_ENVIRONMENTS.includes(explicit)) return explicit;

  if (env.NODE_ENV !== "production" || LOCAL_HOSTNAMES.includes(hostname)) return "development";
  if (hostname.includes("staging")) return "staging";
  return "production";
};

/**
 * @param {Object} options
 * @param {Object} options.env - build-time variables (process.env)
 * @param {string} options.hostname - host the page is opened on
 * @returns {{appEnv: string, serverUrl: string, appUrl: string, moyasarCallbackUrl: string, showTestTools: boolean}}
 */
export const buildEnvironment = ({ env, hostname }) => {
  const appEnv = resolveAppEnv(env, hostname);
  const defaults = URL_DEFAULTS[appEnv];

  return {
    appEnv,
    // API and Socket.IO server, with a trailing slash
    serverUrl: env.REACT_APP_SERVER_URL || defaults.serverUrl,
    // This web app, with a trailing slash
    appUrl: env.REACT_APP_URL || defaults.appUrl,
    moyasarCallbackUrl: env.REACT_APP_MOYASAR_CALLBACK_URL || defaults.moyasarCallbackUrl,
    // Testing helpers (e.g. "Expire in 2 Mins") are never shown on production
    showTestTools: appEnv !== "production",
  };
};

const ENVIRONMENT = buildEnvironment({
  env: {
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_ENV: process.env.REACT_APP_ENV,
    REACT_APP_SERVER_URL: process.env.REACT_APP_SERVER_URL,
    REACT_APP_URL: process.env.REACT_APP_URL,
    REACT_APP_MOYASAR_CALLBACK_URL: process.env.REACT_APP_MOYASAR_CALLBACK_URL,
  },
  hostname: window.location.hostname,
});

export default ENVIRONMENT;
